import React from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  Box,
  Heading,
  Text,
  Button,
  Avatar,
  Alert,
  InfoIcon,
  MailIcon,
  ArrowUpRightIcon,
} from '@razorpay/blade/components';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { MissingReportType } from 'api/types/missingInformationReportTypes';
import { useSendReminder } from '../hooks/useSendReminder';
import { SendReminderModal } from '../SendReminderModal';
import { BankAccountDetailsDrawerProps, BankAccountDetailsDrawerVariant } from '../types';
import { getStatusBadge } from '../viewHelpers';
import { getDrawerMessage } from '../utils';
import { DetailRow } from './DetailRow';

const REMINDER_TYPE_BY_VARIANT: Partial<Record<BankAccountDetailsDrawerVariant, MissingReportType>> =
  {
    incorrect: 'incorrect_bank_account_details',
  };

export const BankAccountDetailsDrawer = ({
  selectedAccount,
  onClose,
  variant,
}: BankAccountDetailsDrawerProps) => {
  const history = useHistory();
  const reminderType = REMINDER_TYPE_BY_VARIANT[variant];
  const { isSendReminderModalOpen, onOpenModal, onCloseModal, sendReminder, isSendingReminder } =
    useSendReminder(reminderType);

  const isRetryVerification = variant === 'unverified_failed';

  const moveToBankAccountForm = () => {
    if (!selectedAccount) {
      return;
    }
    const returnTo = isRetryVerification
      ? routePaths.reports.missingInformation.unverifiedFailed
      : routePaths.reports.missingInformation.incorrect;

    const params = new URLSearchParams();
    params.set('userId', String(selectedAccount.id));
    params.set('returnTo', returnTo);

    const prefillState =
      isRetryVerification && selectedAccount.account_number && selectedAccount.ifsc
        ? {
            prefillBankAccount: {
              accountNumber: selectedAccount.account_number,
              ifscCode: selectedAccount.ifsc,
            },
          }
        : undefined;

    history.push({
      pathname: routePaths.employeeDetailsV2.bankAccountDetails.edit,
      search: `?${params.toString()}`,
      state: prefillState,
    });
    onClose();
  };

  const handleSendReminder = () => {
    if (!selectedAccount || !reminderType) {
      return;
    }
    sendReminder({
      reminder_type: reminderType,
      employee_ids: [Number(selectedAccount.id)],
    });
  };

  const contactLine = selectedAccount
    ? [selectedAccount.email, selectedAccount.phone].filter(Boolean).join(' • ')
    : '';

  const alertColor =
    selectedAccount?.verification_status === 'failed' ||
    selectedAccount?.verification_status === 'invalid'
      ? 'notice'
      : 'information';

  const shouldShowSendReminderButton = variant === 'incorrect';

  return (
    <>
      <Drawer
        isOpen={!!selectedAccount}
        onDismiss={onClose}
        accessibilityLabel={`bank account details for ${selectedAccount?.name}`}>
        <DrawerHeader color="notice" title="Employee">
          {selectedAccount ? (
            <Box display="flex" flexDirection="column">
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="spacing.3"
                marginBottom="spacing.6">
                <Avatar name={selectedAccount.name} size="large" />
                <Heading size="medium" weight="semibold" textAlign="center">
                  {selectedAccount.name}
                </Heading>
                {getStatusBadge(selectedAccount.verification_status)}
              </Box>

              <Alert
                isDismissible={false}
                color={alertColor}
                description={getDrawerMessage(variant, selectedAccount.verification_status)}
                marginBottom="spacing.5"
                emphasis="subtle"
              />

              <Box display="flex" gap="spacing.3" justifyContent="center">
                <Box
                  {...(shouldShowSendReminderButton ? { minWidth: '200px' } : { width: '100%' })}>
                  <Button
                    variant="tertiary"
                    size="medium"
                    isFullWidth
                    onClick={moveToBankAccountForm}
                    icon={ArrowUpRightIcon}>
                    {isRetryVerification ? 'Retry Verification' : 'Update Bank Details'}
                  </Button>
                </Box>
                {shouldShowSendReminderButton && (
                  <Button
                    variant="primary"
                    size="medium"
                    icon={MailIcon}
                    isFullWidth
                    onClick={() => onOpenModal({ remindEmployeeIds: [selectedAccount.id] })}>
                    Send Reminder
                  </Button>
                )}
              </Box>

              {contactLine && (
                <Text
                  color="surface.text.gray.muted"
                  size="small"
                  textAlign="center"
                  marginTop="spacing.5">
                  {contactLine}
                </Text>
              )}
            </Box>
          ) : undefined}
        </DrawerHeader>
        <DrawerBody>
          {selectedAccount && (
            <Box display="flex" flexDirection="column">
              <Heading size="small" weight="semibold" marginBottom="spacing.4">
                Account Details
              </Heading>
              <DetailRow label="Account Number">
                <Text size="medium">{selectedAccount.account_number || '-'}</Text>
              </DetailRow>
              <DetailRow label="IFSC Code">
                <Text size="medium">{selectedAccount.ifsc || '-'}</Text>
              </DetailRow>
              <DetailRow label="Account Holder Name">
                {selectedAccount.account_holder_name ? (
                  <Text size="medium">{selectedAccount.account_holder_name}</Text>
                ) : (
                  <Box display="flex" alignItems="center" gap="spacing.2">
                    <Text size="medium" color="surface.text.gray.muted">
                      Awaiting verification
                    </Text>
                    <BladeTooltipWrapper
                      content="The registered name is fetched from the bank once penny-test verification succeeds."
                      placement="top">
                      <InfoIcon size="small" color="surface.icon.gray.muted" />
                    </BladeTooltipWrapper>
                  </Box>
                )}
              </DetailRow>
            </Box>
          )}
        </DrawerBody>
      </Drawer>
      <SendReminderModal
        isOpen={isSendReminderModalOpen}
        onClose={onCloseModal}
        description="RazorpayX Payroll will email this employee to remind them to update their bank account details."
        onSendReminder={handleSendReminder}
        isSendingReminder={isSendingReminder}
      />
    </>
  );
};
